import sessionTokenStorage from "@/constants/SessionTokenStorage";
import { useAuth } from "@/contexts/AuthContext";
import { CustomThemeContext, type CustomThemeContextData } from "@/contexts/CustomThemeContext";
import { toGluestackTheme } from "@/contexts/defaultTheme";
import { fetchValidPassesData } from "@/services/passes.queries";
import { fetchSessions } from "@/services/sessions.queries";
import { fetchWelcomeData } from "@/services/welcome.queries";
import { useToastNotify } from "@/stores/toastStore";
import { getValueFromStorage } from "@/utils/secureStore";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFocused } from "@react-navigation/native";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@transifex/react";
import { UIModule, WelcomeFailReason } from "ipctg-inflow-types/InFlowEnums";
import type { ParkSessionsPOSTSuccessResponse, ParkValidPassesPOSTSuccessResponse } from "ipctg-inflow-types/InFlowParkAPI";
import { useContext } from "react";

const DEV_QR = "INFLOWTEST";

function useWelcome(args: { qrLookupID: string }) {
  const isFocused = useIsFocused();
  const { user, isLoading } = useAuth();
  const { notify } = useToastNotify();
  const t = useT();
  const { setCustomTheme } = useContext(CustomThemeContext);

  const { data, error, isFetching } = useQuery({
    queryKey: ["welcome", args.qrLookupID, user?.idToken], // Added qr to queryKey
    enabled: isFocused && !isLoading,
    queryFn: async () => {
      // Reset Storage
      const keysToRemove = [
        "licensePlate",
        "receiptID",
        "isPurchasingPass",
        "stripe_session_id",
        "stripe_session_id_paid",
        "lastPurchasePass",
        "lastPay",
        "lastPassRedeem",
      ];
      await AsyncStorage.multiRemove(keysToRemove);

      const activeQr = args.qrLookupID || (await getValueFromStorage("cachedQr"));
      if (!activeQr) {
        throw { failReason: WelcomeFailReason.QRNotFound };
      }
      const welcomeRsp = await fetchWelcomeData({ qr: activeQr });

      sessionTokenStorage.setToken(welcomeRsp.sessionToken);
      await AsyncStorage.multiSet([
        ["cachedQr", activeQr],
        ["skin", JSON.stringify(welcomeRsp.skin)],
      ]);
      setCustomTheme((prevTheme: CustomThemeContextData) => ({
        ...prevTheme,
        skin: welcomeRsp.skin,
        theme: toGluestackTheme(welcomeRsp.skin.theme),
        mode: prevTheme.mode,
      }));

      let sessionsRsp: ParkSessionsPOSTSuccessResponse | null = null;
      let validPassesRsp: ParkValidPassesPOSTSuccessResponse | null = null;

      if (user?.idToken && user.idToken.length > 0) {
        sessionsRsp = await fetchSessions({ cognitoIDToken: user.idToken });
        validPassesRsp = welcomeRsp.uiModules.includes(UIModule.FlexPass) ? await fetchValidPassesData({ cognitoIDToken: user.idToken }) : null;
      }

      return { welcomeRsp, sessionsRsp, validPassesRsp };
    },
    retry: false,
  });

  return { data, error, isFetching };
}

export default useWelcome;
